import * as DateFns from 'date-fns/fp';
import React, { useCallback, useEffect, useState } from 'react';
import styled, { css } from 'styled-components';
import { themeGet } from '@styled-system/theme-get';
import { Select } from '../select/Select';
import { Input } from '../input/Input';
import * as DateUtils from './dateUtils';
import * as TimeUnit from './timeUnit';

const TimeWrapper = styled.div`
  position: relative;
  display: inline-flex;
  justify-content: space-around;
  border-radius: ${themeGet('input.borderRadius')};

  ${props =>
    props.disabled &&
    css`
      ${InputContainer} {
        background-color: ${themeGet('input.disabled.backgroundColor')};
      }
    `}
`;

const InputContainer = styled.div`
  display: inline-flex;
  background-color: ${themeGet('input.backgroundColor')};
  border-radius: ${themeGet('input.borderRadius')};
`;

const StyledDivider = styled.span`
  background-color: ${themeGet('colors.transparent')};
  min-height: 100%;
  width: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

const AMPMSelect = styled(Select)`
  margin-left: 8px;
`;

const StyledInput = styled(Input)`
  background-color: ${themeGet('colors.transparent')};
  text-align: center;
`;

const formatTime = (time, unit) => {
  switch (unit) {
    case TimeUnit.Units.HOUR_24:
      return time.toString().padStart(2, '0');
    case TimeUnit.Units.HOUR_12:
      return DateUtils.get12HourFrom24Hour(time)
        .toString()
        .padStart(2, '0');
    case TimeUnit.Units.MINUTE:
    case TimeUnit.Units.SECOND:
      return time.toString().padStart(2, '0');
    case TimeUnit.Units.MS:
      return time.toString().padStart(3, '0');
    default:
      throw Error('Invalid TimeUnit');
  }
};

const timeUnitOptions = [TimeUnit.ClockFormats.AM, TimeUnit.ClockFormats.PM].map(format => ({
  name: format,
  value: format,
}));

export const TimePicker = ({
  date,
  defaultValue,
  onChange,
  minTime,
  maxTime,
  useAmPm,
  disabled,
  isMobile,
}) => {
  const hourUnit = useAmPm ? TimeUnit.Units.HOUR_12 : TimeUnit.Units.HOUR_24;
  const [currentDate, setCurrentDate] = useState(date || defaultValue);

  useEffect(() => {
    if (date && !DateFns.isEqual(date, currentDate)) {
      setCurrentDate(date);
    }
  }, [currentDate, date]);

  const timeInRange = DateUtils.getTimeInRange(currentDate, minTime, maxTime);
  const isPm = DateUtils.getIsPmFrom24Hour(timeInRange.getHours());
  const [clockFormat, setClockFormat] = useState(isPm ? timeUnitOptions[1] : timeUnitOptions[0]);

  const hourText = formatTime(timeInRange.getHours(), hourUnit);
  const minuteText = formatTime(timeInRange.getMinutes(), TimeUnit.Units.MINUTE);

  const updateTime = useCallback(
    (time, unit) => {
      const newDate = DateUtils.clone(currentDate);

      if (TimeUnit.isTimeUnitValid(unit, time)) {
        TimeUnit.setTimeUnit(unit, time, newDate, isPm);
      }

      if (!date) {
        setCurrentDate(newDate);
      }

      if (onChange && !DateUtils.isSameTime(date, newDate)) {
        onChange({
          target: {
            value: newDate,
          },
        });
      }

      return newDate;
    },
    [currentDate, date, isPm, onChange]
  );

  // select all in input when input on focus
  const handleInputFocus = useCallback(e => {
    e.target.select();
  }, []);

  const handleInputChange = unit => e => {
    const text = e.target.value;
    updateTime(parseInt(text, 10), unit);
  };

  const handleAmPmChange = useCallback(
    format => {
      const { value } = format;
      if (clockFormat.value !== value) {
        const hour = DateUtils.convert24HourMeridiem(
          timeInRange.getHours(),
          value === TimeUnit.ClockFormats.PM
        );

        const newDate = updateTime(hour, TimeUnit.Units.HOUR_24);
        setClockFormat(format);
        setCurrentDate(newDate);

        if (onChange) {
          onChange({
            target: {
              value: newDate,
            },
          });
        }
      }
    },
    [clockFormat.value, timeInRange, updateTime, onChange]
  );

  const handleMobileTimeInputChange = useCallback(
    e => {
      const [hour, min] = e.target.value.split(':');

      updateTime(parseInt(hour, 10), hourUnit);
      updateTime(parseInt(min, 10), TimeUnit.Units.MINUTE);
    },
    [hourUnit, updateTime]
  );

  const currentTime = `${hourText}:${minuteText}`;

  return isMobile ? (
    <input type="time" value={currentTime} onChange={handleMobileTimeInputChange} disabled={disabled} />
  ) : (
    <TimeWrapper disabled={disabled}>
      <InputContainer>
        <StyledInput
          width={40}
          value={hourText}
          onFocus={handleInputFocus}
          onChange={handleInputChange(hourUnit)}
          placeholder="Hour"
          disabled={disabled}
        />
        <StyledDivider>:</StyledDivider>
        <StyledInput
          width={40}
          value={minuteText}
          onFocus={handleInputFocus}
          onChange={handleInputChange(TimeUnit.Units.MINUTE)}
          placeholder="Min"
          disabled={disabled}
        />
      </InputContainer>
      {useAmPm && (
        <AMPMSelect
          width={70}
          value={clockFormat}
          options={timeUnitOptions}
          onChange={handleAmPmChange}
          disabled={disabled}
        />
      )}
    </TimeWrapper>
  );
};

TimePicker.defaultProps = {
  defaultValue: new Date(0, 0, 0, 0, 0, 0, 0),
  maxTime: TimeUnit.getDefaultMaxTime(),
  minTime: TimeUnit.getDefaultMinTime(),
  useAmPm: false,
  isMobile: false,
};
